import React, { useEffect, useRef, useState } from 'react';
import { X, ScanLine, Keyboard } from 'lucide-react';

interface BarcodeScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onScan: (barcode: string) => void;
}

const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({ isOpen, onClose, onScan }) => {
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-focus the input whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        } else {
            setInputValue("");
        }
    }, [isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (inputValue.trim()) {
                onScan(inputValue.trim());
                setInputValue(""); // Clear for next scan
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-[2.5rem] overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-800">
                <div className="p-8 text-center space-y-6">
                    <div className="flex justify-end">
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>

                    <div className="relative inline-flex items-center justify-center">
                        <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full"></div>
                        <div className="relative p-6 bg-indigo-600 rounded-3xl text-white shadow-xl">
                            <ScanLine size={48} className="animate-pulse" />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xl font-black uppercase tracking-tight dark:text-white">Ready to Scan</h3>
                        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.2em] mt-2">
                            Please trigger your hardware scanner now
                        </p>
                    </div>

                    {/* This input is the "catcher". It can be visible or styled to look like a status bar */}
                    <div className="relative group">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Waiting for hardware signal..."
                            className="w-full px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-center font-mono text-sm focus:border-indigo-500 focus:ring-0 transition-all outline-none"
                        />
                        <Keyboard className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    </div>

                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">
                        The item will automatically open upon successful scan
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BarcodeScannerModal;